import { cn } from './cn.js';

export interface PageMeta {
  readonly label: string;
  readonly value: string;
  readonly onClick?: () => void;
}

export interface PageHeaderProps {
  readonly index?: string;
  readonly title: string;
  readonly purpose?: string;
  readonly subtitle?: string;
  readonly hint?: string;
  readonly eyebrow?: string;
  readonly badge?: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly meta?: readonly PageMeta[];
  readonly className?: string;
}

/** Modern SaaS page header with breadcrumb/index, bold title, purpose statement, and KPI chips. */
export function PageHeader({
  index,
  title,
  purpose,
  subtitle,
  hint,
  eyebrow,
  badge,
  actions,
  meta,
  className,
}: PageHeaderProps): React.ReactElement {
  const descriptionText = purpose ?? subtitle ?? hint;
  return (
    <header className={cn('mb-6 animate-rise', className)}>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0 max-w-3xl">
          <div className="flex items-center gap-2">
            {index !== undefined && (
              <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded-md bg-cream text-ink-soft border border-border">
                {index}
              </span>
            )}
            {eyebrow !== undefined && (
              <p className="eyebrow text-herb-700">{eyebrow}</p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              {title}
            </h1>
            {badge !== undefined && <div>{badge}</div>}
          </div>
          {descriptionText !== undefined && (
            <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{descriptionText}</p>
          )}
        </div>
        {actions !== undefined && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>

      {meta !== undefined && meta.length > 0 && (
        <dl className="mt-4 flex flex-wrap items-center gap-2.5 border-t border-border pt-3">
          {meta.map((entry) => {
            const content = (
              <>
                <dt className="eyebrow text-ink-faint">{entry.label}</dt>
                <dd className="font-mono tabular text-xs font-semibold text-ink">
                  {entry.value}
                </dd>
              </>
            );
            return entry.onClick !== undefined ? (
              <button
                key={entry.label}
                type="button"
                onClick={entry.onClick}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 shadow-xs transition hover:border-herb-600 hover:bg-herb-50/40 cursor-pointer text-left focus:outline-none focus:ring-1 focus:ring-herb-600"
                title={`Filtrer par : ${entry.label}`}
              >
                {content}
              </button>
            ) : (
              <div
                key={entry.label}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 shadow-xs"
              >
                {content}
              </div>
            );
          })}
        </dl>
      )}
    </header>
  );
}
